/**
 * Заметки встреч (DEV-1658, переехали из плана в DEV-1973).
 *
 * Заметку пишут во время разговора: одно поле, ни проекта, ни типа, ни срока.
 * Всё, что превращает мысль в задачу, разбирает потом ассистент — а любое
 * обязательное поле в момент записи это повод не записать вовсе.
 *
 * Разобранная заметка не удаляется: по ней потом восстанавливают, откуда взялась
 * задача, и ключи разбора лежат прямо в ней.
 */
import { planApi } from '@/api'
import { formatDueDate, intlLocale } from '@/features/issues/format'

import type { TrackerNote } from '@/api/clients/tracker-plan'

/**
 * Кто записал заметку: человек, модель конспектом или модель действием.
 *
 * Действие лежит отдельной карточкой, а не строкой конспекта: у него свои
 * кнопки и своё состояние, и одной кнопкой пять дел не разбирают (DEV-2096).
 */
export type NoteKind = 'human' | 'ai_summary' | 'ai_action'

/** Что модель предположила о действии. Предположила — решает человек */
export interface NoteAI {
  /** Догадка об исполнителе; пусто, если имя ни с кем не сошлось */
  assigneeUserId: string
  assigneeName: string
  /** Срок в YYYY-MM-DD; пусто, если его не называли */
  dueDate: string
  /** Где в расшифровке это прозвучало — плеер прыгает по смещению */
  quoteOffsetMs: number
  /** Что решил человек: pending, accepted, rejected */
  state: string
  issueKeys: string[]
  /** Говорящих модель угадала: аудио моно, разметки по голосам в нём нет */
  speakersInferred: boolean
}

/** Заметка в том виде, в каком её рисует карточка */
export interface Note {
  id: string
  body: string
  /** Встреча, на которой записана; пусто у личной */
  sessionId: string
  /** Кто записал — у сессионной заметки автор не обязательно смотрящий */
  authorId: string
  authorName: string
  /** Момент разбора; пустой — заметка ещё просто заметка */
  processedAt: string
  /** Ключи задач, которые из неё вышли */
  issueKeys: string[]
  createdAt: string
  kind: NoteKind
  /** Заполнено только у карточки действия */
  ai: NoteAI | null
}

export const toNote = (note: TrackerNote): Note => ({
  id: note.id ?? '',
  body: note.bodyMd ?? '',
  sessionId: note.sessionId ?? '',
  authorId: note.authorId ?? '',
  authorName: note.authorName ?? '',
  processedAt: note.processedAt ?? '',
  issueKeys: note.issueKeys ?? [],
  createdAt: note.createdAt ?? '',
  kind: (note.kind || 'human') as NoteKind,
  ai: note.aiPayload
    ? {
        assigneeUserId: note.aiPayload.assigneeUserId ?? '',
        assigneeName: note.aiPayload.assigneeName ?? '',
        dueDate: note.aiPayload.dueDate ?? '',
        quoteOffsetMs: Number(note.aiPayload.quoteOffsetMs ?? 0),
        state: note.aiPayload.state || 'pending',
        issueKeys: note.aiPayload.issueKeys ?? [],
        speakersInferred: note.aiPayload.speakersInferred ?? false,
      }
    : null,
})

/** Сколько заметок держим на экране: лента справа, дальше — мит-режим */
const NOTES_LIMIT = 50

/**
 * Лента заметок. Сессия сужает её до одной встречи — так её читает мит-режим;
 * без неё приезжают свои личные плюс заметки всех встреч, где человек был.
 */
export interface NotesPage {
  items: Note[]
  /** Сколько заметок всего: длинный протокол на экран не помещается целиком */
  total: number
}

export const listNotes = async (sessionId = ''): Promise<NotesPage> => {
  const { data } = await planApi.planServiceListNotes(
    false,
    '1',
    String(NOTES_LIMIT),
    sessionId || undefined,
  )

  return {
    items: (data.notes ?? []).map(toNote),
    total: Number(data.pageInfo?.total ?? 0),
  }
}

export const createNote = async (body: string, sessionId = ''): Promise<Note | null> => {
  const { data } = await planApi.planServiceCreateNote({
    bodyMd: body,
    sessionId: sessionId || undefined,
  })

  return data.id ? toNote(data) : null
}

/** Что правим в заметке: текст, пометку разбора и ключи вышедших из неё задач */
export interface NotePatch {
  body?: string
  processed?: boolean
  issueKeys?: string[]
  /**
   * Решение человека по карточке действия: accepted (задача заведена),
   * rejected (не нужно) или pending (вернуть). `accepted` без ключей бэкенд не
   * примет: карточка, обещающая несуществующую задачу, хуже её отсутствия.
   */
  aiState?: string
}

export const updateNote = async (id: string, patch: NotePatch): Promise<Note | null> => {
  const { data } = await planApi.planServiceUpdateNote(id, {
    bodyMd: patch.body,
    processed: patch.processed,
    issueKeys: patch.issueKeys,
    aiState: patch.aiState,
  })

  return data.id ? toNote(data) : null
}

export const deleteNote = async (id: string): Promise<void> => {
  await planApi.planServiceDeleteNote(id)
}

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/**
 * Время заметки — «14:05 · сегодня» (макет 9-5).
 *
 * Часы впереди дня, а не наоборот, как у события задачи: заметки за один
 * разговор ложатся подряд одним днём, и различает их именно время. У давней дата
 * заменяет слово, но час остаётся на месте — колонка не должна прыгать.
 */
export const formatNoteStamp = (
  iso: string,
  locale: string,
  labels: { today: string; yesterday: string },
  now = new Date(),
): string => {
  if (!iso) return ''

  const date = new Date(iso)

  if (Number.isNaN(date.getTime())) return ''

  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  const time = new Intl.DateTimeFormat(intlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)

  if (days === 0) return `${time} · ${labels.today}`
  if (days === 1) return `${time} · ${labels.yesterday}`

  return `${time} · ${formatDueDate(iso, locale, now)}`
}
